import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Container,
  Button,
  FormFeedback,
  Alert,
} from "reactstrap";
import { FormikProvider, useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import type { PurchaseOrder } from "features/PurchaseOrders/types/purchase-order.types";
import {
  createPurchaseOrder,
  getPurchaseOrder,
  updatePurchaseOrder,
} from "features/PurchaseOrders/services/PurchaseOrderService";
import { PurchaseOrderStatus } from "features/PurchaseOrders/constants/purchase-order.constants";
import { getDocumentTitle } from "utils/helpers";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getSupplierOptions } from "features/Suppliers/services/SupplierService";
import {
  SupplierOption,
  SupplierSingleValue,
} from "features/Suppliers/components/SupplierSelectComponents";
import PurchaseOrderBoqFieldArray from "features/PurchaseOrders/components/PurchaseOrderBoqFieldArray";
import PageLoadingSpinner from "components/common/PageLoader";
import {
  poBoqRowsToLinePayload,
  purchaseOrderItemsToBoqRows,
} from "features/PurchaseOrders/utils/purchase-order.utils";
import {
  localDateToYmd,
  ymdToLocalDate,
} from "features/PurchaseOrders/utils/purchase-order-dates.utils";
import Flatpickr from "react-flatpickr";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  getMaterialRequest,
  getMaterialRequestOptions,
} from "features/MaterialRequests/services/MaterialRequestService";
import {
  MrSelectOption,
  MrSelectMultiValue,
} from "features/MaterialRequests/components/MaterialRequestSelectComponents";
import { materialRequestItemsToPurchaseOrderBoqRows } from "features/PurchaseOrders/utils/purchase-order-from-material-request.utils";
import type { SelectOption } from "types/api.types";
import { formatCurrency } from "utils/currency";

const breadcrumbItems = [
  { label: "Purchase orders", path: "/inventory-management/purchase-orders" },
];

const PurchaseOrderForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(
    !isEdit ? "New purchase order" : "Edit purchase order",
  );
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAccess();
  const canReadMaterialRequests = can(Permissions.MATERIAL_REQUEST.READ);
  const [record, setRecord] = useState<PurchaseOrder | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(Boolean(isEdit));
  const [selectedMrIds, setSelectedMrIds] = useState<number[]>([]);
  const [prefillBusy, setPrefillBusy] = useState(false);

  const isLocked =
    record != null && record.status !== PurchaseOrderStatus.DRAFT;

  /** Line items are always read-only; only the per-line supplier select stays interactive. */
  const lineItemsReadOnly = true;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      supplierId: record?.supplierId ?? ("" as number | ""),
      orderDate: record?.orderDate ? String(record.orderDate).slice(0, 10) : "",
      expectedDate: record?.expectedDate
        ? String(record.expectedDate).slice(0, 10)
        : "",
      notes: record?.notes ?? record?.remarks ?? "",
      actualAmount:
        record?.actualAmount != null ? String(record.actualAmount) : "",
      items:
        isEdit && record?.items?.length
          ? purchaseOrderItemsToBoqRows(record.items)
          : [],
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const lines = poBoqRowsToLinePayload(values.items);
      if (!lines.length) {
        toast.error("At least one material line with a product is required.");
        return;
      }

      try {
        if (!isEdit) {
          const supplierIdValue = Number(values.supplierId);
          const res = await createPurchaseOrder({
            ...(Number.isFinite(supplierIdValue) && supplierIdValue > 0
              ? { supplierId: supplierIdValue }
              : {}),
            orderDate: values.orderDate,
            expectedDate: values.expectedDate?.trim()
              ? values.expectedDate
              : null,
            notes: values.notes?.trim() || null,
            ...(selectedMrIds.length > 0
              ? { materialRequestIds: selectedMrIds }
              : {}),
            items: lines,
          });
          if (res.success) {
            toast.success(res.message);
            navigate(-1);
          } else toast.error(res.message);
          return;
        }

        if (!id) return;
        const supplierIdForUpdate = Number(values.supplierId);
        const res = await updatePurchaseOrder(Number(id), {
          ...(Number.isFinite(supplierIdForUpdate) && supplierIdForUpdate > 0
            ? { supplierId: supplierIdForUpdate }
            : { supplierId: null }),
          orderDate: values.orderDate,
          expectedDate: values.expectedDate?.trim()
            ? values.expectedDate
            : null,
          notes: values.notes?.trim() || null,
          actualAmount: String(values.actualAmount ?? "").trim()
            ? Number(values.actualAmount)
            : null,
          items: lines,
        });
        if (res.success) {
          toast.success(res.message);
          navigate(-1);
        } else toast.error(res.message);
      } catch (err: unknown) {
        console.log(err);
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Request failed",
        );
      }
    },
  });

  useEffect(() => {
    if (!isEdit || !id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingRecord(true);
        const res = await getPurchaseOrder(Number(id));
        if (cancelled) return;
        if (res.success && res.data) {
          setRecord(res.data);
          if (res.data.status !== PurchaseOrderStatus.DRAFT) {
            toast.info("This purchase order is not editable.");
          }
        }
      } catch {
        if (!cancelled) setRecord(null);
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  if (loadingRecord) {
    return <PageLoadingSpinner message="Loading purchase order…" />;
  }

  if (isEdit && record && isLocked) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Edit purchase order"
            pageTitle="Inventory Management"
            items={breadcrumbItems}
          />
          <Row className="justify-content-center">
            <Col xl={9} lg={10}>
              <Card className="shadow-none border">
                <CardBody className="pt-4">
                  <Alert color="warning" className="mb-0" role="alert">
                    <i className="ri-information-line me-2 align-middle" />
                    Only draft purchase orders can be edited. Open the overview
                    to view this order or use the list to find a draft.
                  </Alert>
                </CardBody>
                <CardFooter className="bg-light bg-opacity-50 border-top py-3">
                  <BackButton />
                </CardFooter>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const supplierDefault =
    record?.supplier && record.supplier.id
      ? {
          label: [record.supplier.code, record.supplier.name]
            .filter(Boolean)
            .join(" · "),
          value: record.supplier.id,
        }
      : undefined;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isEdit ? "Edit purchase order" : "New purchase order"}
          pageTitle="Inventory Management"
          items={breadcrumbItems}
        />
        <FormikProvider value={validation}>
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <Row className="justify-content-center">
              <Col xl={10}>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-1">
                      {isEdit ? "Edit draft order" : "Create purchase order"}
                    </h5>
                    <p className="text-muted mb-0 small">
                      Choose a supplier and dates. For new orders, load line
                      items from a material request (remaining quantities); line
                      items are read-only. Drafts linked to a request stay
                      read-only; other drafts can be edited like a BOQ.
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Order header
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6}>
                        <Label className="form-label">Supplier</Label>
                        <AsyncPaginatedSelect
                          value={validation.values.supplierId || null}
                          onChange={(opt) =>
                            validation.setFieldValue(
                              "supplierId",
                              opt ? (opt as { value: number }).value : "",
                            )
                          }
                          onBlur={() =>
                            validation.setFieldTouched("supplierId", true)
                          }
                          fetchOptions={getSupplierOptions}
                          placeholder="Search suppliers…"
                          isDisabled={isLocked}
                          defaultOption={supplierDefault}
                          components={{
                            Option: SupplierOption,
                            SingleValue: SupplierSingleValue,
                          }}
                          isInvalid={
                            validation.touched.supplierId &&
                            Boolean(validation.errors.supplierId)
                          }
                        />
                        {validation.touched.supplierId &&
                        validation.errors.supplierId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(validation.errors.supplierId)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={3}>
                        <Label className="form-label" htmlFor="po-order-date">
                          Order date
                        </Label>
                        <Flatpickr
                          id="po-order-date"
                          name="orderDate"
                          className={`form-control ${
                            validation.errors.orderDate &&
                            validation.touched.orderDate
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select order date"
                          options={{
                            altInput: true,
                            altFormat: "d M Y",
                            dateFormat: "Y-m-d",
                          }}
                          value={ymdToLocalDate(validation.values.orderDate)}
                          onChange={(dates: Date[]) => {
                            validation.setFieldTouched("orderDate", true);
                            if (!dates?.length) {
                              validation.setFieldValue("orderDate", "");
                              return;
                            }
                            validation.setFieldValue(
                              "orderDate",
                              localDateToYmd(dates[0]),
                            );
                          }}
                          onClose={() =>
                            validation.setFieldTouched("orderDate", true)
                          }
                          disabled={isLocked}
                        />
                        {validation.touched.orderDate &&
                        validation.errors.orderDate ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.orderDate)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={3}>
                        <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
                          <Label
                            className="form-label mb-0"
                            htmlFor="po-expected-date"
                          >
                            Expected date{" "}
                            <span className="text-muted fw-normal">
                              (optional)
                            </span>
                          </Label>
                          {validation.values.expectedDate && !isLocked ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-muted py-0 px-0"
                              onClick={() => {
                                validation.setFieldValue("expectedDate", "");
                                validation.setFieldTouched(
                                  "expectedDate",
                                  true,
                                );
                              }}
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                        <Flatpickr
                          id="po-expected-date"
                          name="expectedDate"
                          className={`form-control mt-1${
                            validation.errors.expectedDate &&
                            validation.touched.expectedDate
                              ? " is-invalid"
                              : ""
                          }`}
                          placeholder="Select expected date"
                          options={{
                            altInput: true,
                            altFormat: "d M Y",
                            dateFormat: "Y-m-d",
                          }}
                          value={ymdToLocalDate(validation.values.expectedDate)}
                          onChange={(dates: Date[]) => {
                            validation.setFieldTouched("expectedDate", true);
                            if (!dates?.length) {
                              validation.setFieldValue("expectedDate", "");
                              return;
                            }
                            validation.setFieldValue(
                              "expectedDate",
                              localDateToYmd(dates[0]),
                            );
                          }}
                          onClose={() =>
                            validation.setFieldTouched("expectedDate", true)
                          }
                          disabled={isLocked}
                        />
                        {validation.touched.expectedDate &&
                        validation.errors.expectedDate ? (
                          <FormFeedback type="invalid" className="d-block">
                            {String(validation.errors.expectedDate)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col xs={12}>
                        <Label className="form-label" htmlFor="po-notes">
                          Notes{" "}
                          <span className="text-muted fw-normal">
                            (optional)
                          </span>
                        </Label>
                        <Input
                          id="po-notes"
                          type="textarea"
                          rows={2}
                          name="notes"
                          placeholder="Delivery instructions, internal notes…"
                          value={validation.values.notes}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          disabled={isLocked}
                          invalid={Boolean(
                            validation.touched.notes && validation.errors.notes,
                          )}
                        />
                        {validation.touched.notes && validation.errors.notes ? (
                          <FormFeedback type="invalid">
                            {String(validation.errors.notes)}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      {isEdit ? (
                        <Col md={4}>
                          <Label
                            className="form-label"
                            htmlFor="po-actual-amount"
                          >
                            Actual amount{" "}
                            <span className="text-muted fw-normal">
                              (optional)
                            </span>
                          </Label>
                          <Input
                            id="po-actual-amount"
                            type="number"
                            min={0}
                            step="0.01"
                            name="actualAmount"
                            placeholder="0.00"
                            value={validation.values.actualAmount}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          />
                          <small className="text-muted">
                            The actual spend recorded after fulfillment.
                          </small>
                        </Col>
                      ) : null}
                    </Row>

                    <hr className="my-4 text-muted opacity-25" />

                    {!isEdit && !isLocked && canReadMaterialRequests ? (
                      <>
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                          Prefill from material requests
                        </div>
                        <Row className="g-3 mb-4">
                          <Col lg={8}>
                            <Label className="form-label">
                              Material requests
                            </Label>
                            <AsyncPaginatedSelect
                              fetchOptions={getMaterialRequestOptions}
                              placeholder="Search by code, id, or notes…"
                              requestParams={{ withoutActivePo: true, status: "APPROVED" }}
                              value={selectedMrIds}
                              isMulti
                              components={{ Option: MrSelectOption, MultiValue: MrSelectMultiValue }}
                              styles={{
                                multiValue: (base: any) => ({
                                  ...base,
                                  backgroundColor: "var(--vz-primary-bg-subtle)",
                                }),
                                multiValueLabel: (base: any) => ({
                                  ...base,
                                  color: "var(--vz-primary)",
                                  fontWeight: 500,
                                }),
                                multiValueRemove: (base: any) => ({
                                  ...base,
                                  color: "var(--vz-primary)",
                                }),
                              }}
                              isDisabled={
                                prefillBusy || validation.isSubmitting
                              }
                              onChange={async (opts) => {
                                const selected = Array.isArray(opts)
                                  ? (opts as SelectOption[])
                                  : [];
                                const newIds = selected
                                  .map((o) => Number(o.value))
                                  .filter(
                                    (id) => Number.isFinite(id) && id > 0,
                                  );
                                const isRemoving = newIds.length < selectedMrIds.length;
                                setSelectedMrIds(newIds);

                                if (!newIds.length) {
                                  validation.setFieldValue("items", []);
                                  return;
                                }

                                setPrefillBusy(true);
                                try {
                                  const results = await Promise.all(
                                    newIds.map((id) => getMaterialRequest(id)),
                                  );
                                  const allLines = results.flatMap((res) =>
                                    res.success && res.data
                                      ? materialRequestItemsToPurchaseOrderBoqRows(
                                          res.data.items,
                                        )
                                      : [],
                                  );
                                  if (!allLines.length) {
                                    toast.info(
                                      "No orderable lines across the selected requests.",
                                    );
                                    return;
                                  }
                                  validation.setFieldValue("items", allLines);
                                  if (
                                    !String(
                                      validation.values.expectedDate ?? "",
                                    ).trim()
                                  ) {
                                    const firstDate = results
                                      .map((r) => r.data?.requiredDate)
                                      .find(Boolean);
                                    if (firstDate) {
                                      validation.setFieldValue(
                                        "expectedDate",
                                        String(firstDate).slice(0, 10),
                                      );
                                    }
                                  }
                                  toast.success(
                                    isRemoving
                                      ? `Lines reloaded from ${newIds.length} material request(s).`
                                      : `Lines loaded from ${newIds.length} material request(s).`,
                                  );
                                } catch (err: unknown) {
                                  toast.error(
                                    (
                                      err as {
                                        response?: {
                                          data?: { message?: string };
                                        };
                                      }
                                    )?.response?.data?.message ??
                                      "Could not load material requests.",
                                  );
                                } finally {
                                  setPrefillBusy(false);
                                }
                              }}
                            />
                            <p className="text-muted small mb-0 mt-1">
                              Only <strong>Approved</strong> requests can be used.
                              Lines from all selected requests are merged and
                              replace the current line items.
                            </p>
                          </Col>
                        </Row>
                      </>
                    ) : null}

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                      Line items
                    </div>
                    {lineItemsReadOnly && !isEdit ? (
                      <p className="text-muted small mb-3">
                        Select a material request above to load lines. They stay
                        read-only so the order matches the request.
                      </p>
                    ) : null}
                    {validation.touched.items &&
                    typeof validation.errors.items === "string" ? (
                      <div className="text-danger small mb-2">
                        {validation.errors.items}
                      </div>
                    ) : null}

                    <div className="border rounded p-3 bg-light bg-opacity-25">
                      <PurchaseOrderBoqFieldArray
                        validation={validation}
                        readOnly={lineItemsReadOnly || isLocked}
                        embedded
                      />
                    </div>

                    {(() => {
                      const lineTotal = (
                        validation.values.items as any[]
                      ).reduce(
                        (sum: number, item: any) =>
                          sum +
                          (Number(item.rate) || 0) *
                            (Number(item.quantity) || 0),
                        0,
                      );
                      if (!lineTotal) return null;
                      return (
                        <div className="d-flex justify-content-end mt-2">
                          <div className="px-3 py-2 rounded border bg-light d-flex align-items-center gap-3">
                            <span className="text-muted small text-uppercase fw-semibold">
                              Estimated total
                            </span>
                            <span className="fw-semibold fs-15">
                              {formatCurrency(lineTotal)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    {!isLocked ? (
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={validation.isSubmitting || prefillBusy}
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            {isEdit ? "Saving…" : "Creating…"}
                          </>
                        ) : isEdit ? (
                          "Save changes"
                        ) : (
                          "Create"
                        )}
                      </Button>
                    ) : null}
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </FormikProvider>
      </Container>
    </div>
  );
};

export default PurchaseOrderForm;
